import {
  createReducer,
  EntityState,
  isAnyOf,
} from '@reduxjs/toolkit'

import {
  FeatureKey,
  getFeatureKeysObject,
} from '@/constants/feature-key'
import { NormalStatus } from '@/constants/normal-status'
import type { Creator } from '@/model/creator'
import {
  activateCreator,
  createCreator,
  creatorNamespace,
  deactivateCreator,
  readAllCreators,
  refreshCreatorAuthToken,
  removeCreator,
  setSelectedCreatorId,
  updateCreator,
} from '@/redux/creator-actions'
import { creatorAdapter } from '@/redux/creator-adaptor'
import { initBaeTokenMap } from '@/utils/axios'

const initialState: {
  selectedCreatorId?: number
  permissionMap: Record<FeatureKey, number[]>
  creatorEntity: EntityState<Creator, number>
  creatorsFetching: boolean
} = {
  permissionMap: getFeatureKeysObject([]),
  creatorEntity: creatorAdapter.getInitialState(),
  creatorsFetching: false,
}

export type CreatorState = typeof initialState

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readAllCreators.pending, (draft) => {
    draft.creatorsFetching = true
  })
  builder.addCase(readAllCreators.rejected, (draft) => {
    draft.creatorsFetching = false
  })
  builder.addCase(readAllCreators.fulfilled, (draft, { payload: { data, permissionKey } }) => {
    draft.creatorsFetching = false
    creatorAdapter.upsertMany(draft.creatorEntity, data.rows)
    draft.permissionMap[permissionKey] = data.rows.map(({ id }) => id)

    if ((!draft.selectedCreatorId || data.rows.every((creator) => creator.id !== draft.selectedCreatorId)) && data.rows.length > 0) {
      draft.selectedCreatorId = data.rows.find(({ status }) => status === NormalStatus.Activated)?.id
    }
  })
  builder.addCase(createCreator.fulfilled, (draft, { payload: { data, permissionKey } }) => {
    draft.permissionMap[permissionKey].push(data.id)
    if (!draft.selectedCreatorId) {
      draft.selectedCreatorId = data.id
    }
    creatorAdapter.addOne(draft.creatorEntity, data)
  })
  builder.addCase(removeCreator.fulfilled, (draft, { meta }) => {
    const removedCreatorId = meta.arg
    Object.values(draft.permissionMap).forEach((ids) => {
      const index = ids.findIndex((id) => id === removedCreatorId)
      if (index !== -1) {
        ids.splice(index, 1)
      }
    })
    creatorAdapter.removeOne(draft.creatorEntity, removedCreatorId)
  })
  builder.addCase(setSelectedCreatorId, (draft, { payload }) => {
    draft.selectedCreatorId = payload
  })

  builder.addCase(refreshCreatorAuthToken.fulfilled, (draft, { payload }) => {
    creatorAdapter.updateOne(draft.creatorEntity, { changes: payload, id: payload.creatorId })
    initBaeTokenMap(payload)
  })

  builder.addMatcher(isAnyOf(
    updateCreator.fulfilled,
    activateCreator.fulfilled,
    deactivateCreator.fulfilled,
  ), (draft, { payload }) => {
    creatorAdapter.updateOne(draft.creatorEntity, { changes: payload, id: payload.id })
  })
})

export default { [creatorNamespace]: reducer }
